import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import sharpFlatIcon from '@Assets/images/sharp-flat.svg';
import fingeringIcon from '@Assets/images/fingering.svg';
import altFingerIcon from '@Assets/images/alt-finger.svg';
import pitchLetterIcon from '@Assets/images/pitch-letter.svg';
import pencilEditIcon from '@Assets/images/pencil-edit.svg';
import { SCORE_COLUMN_WIDTH_VAR } from '../../utils/scoreZoom';
import type { PitchMode, PitchScope } from '../../model/pitchVisibility';
import { PitchModeMenu } from './PitchModeMenu';

export interface ScoreSideTogglesProps {
  showAccidental: boolean;
  showAltFingerings: boolean;
  showPitch: boolean;
  /**
   * Which notes show a letter while pitch is on. Required with
   * `onSelectPitchMode`. The scan page omits both and uses
   * `onTogglePitch` instead.
   */
  pitchScope?: PitchScope;
  showFingerings: boolean;
  /** When false, the Accidental + Pitch pills are hidden because the
   *  underlying project predates the noteheadDataList payload. */
  hasNoteheadData: boolean;

  onToggleAccidental: () => void;
  onToggleAlt: () => void;
  /** Plain on/off pitch pill. Used when the page has no pitch modes. */
  onTogglePitch?: () => void;
  /** Off / ledger / all menu. Takes precedence over `onTogglePitch`. */
  onSelectPitchMode?: (mode: PitchMode) => void;
  onToggleFingerings: () => void;

  /** Pencil pill is rendered only when fingerings and alts are both
   *  on. Drag-and-swap requires those plus `isEditMode`. */
  onTogglePencil: () => void;
  isEditMode: boolean;
  /** Playback owns note taps, so the pencil cannot enter edit. */
  editDisabled?: boolean;

  /** Engraved view only. Hidden when the callback is omitted. */
  showPlayback?: boolean;
  isPlaybackMode?: boolean;
  onTogglePlayback?: () => void;
  /** Edit mode owns note taps, so playback cannot be entered. */
  playbackDisabled?: boolean;

  /** `vertical` (default) is the fixed right-gutter column used on
   *  `md`+ screens. `horizontal` renders the same pills in a plain
   *  in-flow row for embedding in the mobile bottom bar. */
  orientation?: 'vertical' | 'horizontal';
}

interface SidePillProps {
  iconSrc?: string;
  faIcon?: IconDefinition;
  iconAlt: string;
  label: string;
  pressed: boolean;
  onClick: () => void;
  disabled?: boolean;
  /** Override the icon's display size (px). Some glyphs render small
   *  intrinsically — e.g. the pitch icon is taller than the others. */
  iconClassName?: string;
  /** Optional `data-tour` hook used by the onboarding tutorial to
   *  spotlight this pill. */
  dataTour?: string;
  buttonRef?: React.Ref<HTMLButtonElement>;
  ariaExpanded?: boolean;
}

/**
 * Pill button that is icon-only at rest and reveals its label on
 * hover/focus via a width transition. Tailwind's `group-hover` /
 * `group-focus-visible` lets us drive the label visibility from the
 * surrounding pill without JS state. The label sits inside a
 * `max-w-0`/`max-w-[200px]` clip so the pill smoothly expands.
 *
 * Layout strategy — the icon STAYS PUT when the pill expands:
 *
 *   The icon lives inside a fixed 44×44 "slot" at the start of the
 *   pill (`flex h-11 w-11 items-center justify-center shrink-0`).
 *   The label sits in a separate flex child immediately to its
 *   right. Because the slot keeps its own width regardless of what
 *   the label is doing, the icon's horizontal position never shifts
 *   when the label expands or collapses — it just stays anchored at
 *   the pill's left edge while the pill grows rightward into the
 *   gutter.
 *
 *   This is the cleanest way to avoid the "icon slides to the left"
 *   effect that happens when the whole pill uses `justify-center`
 *   and the label expansion re-centers the icon+label block.
 *
 *   Icon sizing: `iconClassName` is forwarded directly to the
 *   `<img>`. Sizes up to 44px fit naturally inside the slot; larger
 *   icons would overflow it (and stretch the pill out of round).
 *   Default size is `h-6 w-6` (24px) — the slot's 10px of padding
 *   on each side gives the icon its visual "breathing room".
 */
const SidePill: React.FC<SidePillProps> = ({
  iconSrc,
  faIcon,
  iconAlt,
  label,
  pressed,
  onClick,
  disabled = false,
  iconClassName,
  dataTour,
  buttonRef,
  ariaExpanded,
}) => {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaExpanded === undefined ? undefined : 'menu'}
      title={label}
      data-tour={dataTour}
      className={`
        group flex h-11 items-center rounded-full ring-1 ring-black/5
        transition-colors
        focus:outline-none
        ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}
        ${
          pressed
            ? 'bg-[#5C6BFF] hover:bg-[#4A58E6] disabled:hover:bg-[#5C6BFF]'
            : 'bg-white hover:bg-gray-50 disabled:hover:bg-white'
        }
      `}
    >
      {/* Fixed 44×44 icon slot — its width never changes, so the
          icon stays in the same horizontal position regardless of
          whether the label is expanded. Centering happens locally
          inside the slot via `items-center justify-center`. */}
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center`}>
        {/* Glyphs are dark (#333). CSS filters normalize them per state:
              - off  → `brightness(0)`           = solid black glyph
              - on   → `brightness(0) invert(1)` = solid white glyph
            on the #5C6BFF pill. */}
        {faIcon ? (
          <FontAwesomeIcon
            icon={faIcon}
            title={iconAlt || undefined}
            className={`
              shrink-0 text-[22px]
              ${iconClassName ?? ''}
              ${pressed ? 'text-white' : 'text-gray-800'}
            `}
          />
        ) : (
          <img
            src={iconSrc}
            alt={iconAlt}
            draggable={false}
            className={`
              shrink-0
              ${iconClassName ?? 'h-6 w-6 object-contain'}
              ${
                pressed
                  ? '[filter:brightness(0)_invert(1)]'
                  : '[filter:brightness(0)]'
              }
            `}
          />
        )}
      </span>
      {/* Label expansion is hover-only (plus keyboard `focus-visible`
          for accessibility). We deliberately avoid `group-focus-within`
          here because that variant matches mouse-click focus too,
          which would keep the label expanded after the user clicks
          the pill and moves the cursor away. `focus-visible` only
          fires for keyboard focus, so click → move-away now collapses
          the pill back to icon-only as expected.

          The label is a SEPARATE flex child to the right of the icon
          slot. Because the slot owns its own 44px width, the label's
          0→full-width transition only grows the pill rightward — the
          icon never shifts. */}
      <span
        className={`
          max-w-0 overflow-hidden pr-0 text-sm whitespace-nowrap opacity-0
          transition-all duration-200 ease-out
          group-hover:max-w-[200px] group-hover:pr-4 group-hover:opacity-100
          group-focus-visible:max-w-[200px] group-focus-visible:pr-4
          group-focus-visible:opacity-100
          ${pressed ? 'text-white' : 'text-gray-800'}
        `}
      >
        {label}
      </span>
    </button>
  );
};

/**
 * Stacks two or more pills and paints the same connector bar that
 * already links Alternate Fingerings to Edit. The bar is absolute, so
 * it does not affect layout. `top-[22px] bottom-[22px]` pins it to the
 * first pill's center and the last pill's center — visible only in the
 * 12px gaps and the inner-corner slivers of each `rounded-full`.
 */
const ConnectedPills: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    className={`relative isolate flex min-w-11 flex-col items-start gap-y-3`}
  >
    <div
      aria-hidden
      className={`
        pointer-events-none absolute top-[22px] bottom-[22px] left-0 -z-10 w-11
        bg-[#C8CCFF]
      `}
    />
    {children}
  </div>
);

const PitchPill: React.FC<{
  showPitch: boolean;
  pitchScope: PitchScope;
  onSelectPitchMode: (mode: PitchMode) => void;
  placement: 'side' | 'top';
}> = ({ showPitch, pitchScope, onSelectPitchMode, placement }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mode: PitchMode = showPitch ? pitchScope : 'off';
  return (
    <div className="relative">
      <SidePill
        iconSrc={pitchLetterIcon}
        iconAlt=""
        label={mode === 'ledger' ? 'Pitch · partial' : 'Pitch'}
        pressed={mode !== 'off'}
        onClick={() => setOpen((value) => !value)}
        iconClassName="h-6 w-6 object-contain"
        dataTour="pitch"
        buttonRef={buttonRef}
        ariaExpanded={open}
      />
      {open && (
        <PitchModeMenu
          mode={mode}
          placement={placement}
          anchorRef={buttonRef}
          onClose={() => setOpen(false)}
          onSelect={(next) => {
            onSelectPitchMode(next);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
};

/**
 * Vertical column of pill toggles fixed just outside the right edge of
 * the centered score container. `--score-column-width` is published by
 * `useScoreZoom` (resting max-w-7xl, then growing toward 90vw). The
 * pills sit at `column_right + 1rem`, clamped so they never leave the
 * viewport:
 *
 *   left = min(calc(50vw + var(--score-column-width) / 2 + 1rem),
 *              calc(100vw - 3.75rem))
 *
 * Stays visible while `isEditMode` is true so the pencil pill can show its
 * pressed state and be tapped again to exit editing.
 */
export const ScoreSideToggles: React.FC<ScoreSideTogglesProps> = ({
  showAccidental,
  showAltFingerings,
  showPitch,
  pitchScope = 'all',
  showFingerings,
  hasNoteheadData,
  onToggleAccidental,
  onToggleAlt,
  onTogglePitch,
  onSelectPitchMode,
  onToggleFingerings,
  onTogglePencil,
  isEditMode,
  editDisabled = false,
  showPlayback = false,
  isPlaybackMode = false,
  onTogglePlayback,
  playbackDisabled = false,
  orientation = 'vertical',
}) => {
  const pitchControl = !hasNoteheadData ? null : onSelectPitchMode ? (
    <PitchPill
      showPitch={showPitch}
      pitchScope={pitchScope}
      onSelectPitchMode={onSelectPitchMode}
      placement={orientation === 'horizontal' ? 'top' : 'side'}
    />
  ) : onTogglePitch ? (
    <SidePill
      iconSrc={pitchLetterIcon}
      iconAlt=""
      label="Pitch"
      pressed={showPitch}
      onClick={onTogglePitch}
      iconClassName="h-6 w-6 object-contain"
      dataTour="pitch"
    />
  ) : null;
  const playbackPill =
    showPlayback && onTogglePlayback ? (
      <SidePill
        faIcon={faPlay}
        iconAlt=""
        label="Playback"
        pressed={isPlaybackMode}
        disabled={playbackDisabled}
        onClick={onTogglePlayback}
        dataTour="playback"
      />
    ) : null;
  // Horizontal variant: a plain in-flow row of the same pills, used by
  // the mobile bottom bar. No fixed positioning and no vertical
  // connector rectangle (that decoration only makes sense stacked
  // vertically) — the alt + pencil pills simply sit next to each other.
  if (orientation === 'horizontal') {
    return (
      <div className="flex flex-row items-center gap-x-1">
        {hasNoteheadData && (
          <SidePill
            iconSrc={sharpFlatIcon}
            iconAlt=""
            label="Sharps & Flats"
            pressed={showAccidental}
            onClick={onToggleAccidental}
            iconClassName="h-5 w-5 object-contain"
            dataTour="sharps"
          />
        )}
        <SidePill
          iconSrc={fingeringIcon}
          iconAlt=""
          label="Piano fingering"
          iconClassName="h-7 w-7 object-contain"
          pressed={showFingerings}
          onClick={onToggleFingerings}
          dataTour="fingerings"
        />
        {showFingerings && (
          <SidePill
            iconSrc={altFingerIcon}
            iconClassName="h-6 w-6 object-contain"
            iconAlt=""
            label="Alternate Fingerings"
            pressed={showAltFingerings}
            onClick={onToggleAlt}
            dataTour="alt"
          />
        )}
        {showFingerings && showAltFingerings && (
          <SidePill
            iconSrc={pencilEditIcon}
            iconAlt=""
            label="Edit fingerings"
            pressed={isEditMode}
            disabled={editDisabled}
            onClick={onTogglePencil}
            iconClassName="h-5 w-5 object-contain"
            dataTour="edit"
          />
        )}
        {playbackPill}
        {pitchControl}
      </div>
    );
  }

  return (
    <div
      className={`pointer-events-none fixed z-40 hidden md:block`}
      style={{
        left: `min(calc(50vw + var(${SCORE_COLUMN_WIDTH_VAR}, 80rem) / 2 + 1rem), calc(100vw - 3.75rem))`,
        // Anchor from the column's TOP (no vertical centering) with a floor
        // that clears the site navbar (4rem) + sticky ProjectToolbar so the
        // top pill is never hidden behind the toolbar on short/small screens.
        // On tall viewports the 30% keeps its lower, balanced desktop position.
        top: 'max(9.5rem, 30%)',
      }}
    >
      <div
        className={`pointer-events-auto flex w-48 flex-col items-start gap-y-3`}
      >
        {hasNoteheadData && (
          <SidePill
            iconSrc={sharpFlatIcon}
            iconAlt=""
            label="Sharps & Flats"
            pressed={showAccidental}
            onClick={onToggleAccidental}
            iconClassName="h-5 w-5 object-contain"
            dataTour="sharps"
          />
        )}
        {/* Fingerings → Alternate Fingerings → Edit grouping.
         *
         *  Fingerings ON shows Alternate Fingerings below it, linked
         *  by the same connector bar that already linked alt to Edit.
         *  Alternate Fingerings ON then adds Edit to the same stack,
         *  so the bar runs through all three. Fingerings OFF drops
         *  back to the standalone pill — a bar on one toggle would
         *  have nothing to connect to. */}
        {!showFingerings ? (
          <SidePill
            iconSrc={fingeringIcon}
            iconAlt=""
            label="Piano fingering"
            iconClassName="h-7 w-7 object-contain"
            pressed={showFingerings}
            onClick={onToggleFingerings}
            dataTour="fingerings"
          />
        ) : (
          <ConnectedPills>
            <SidePill
              iconSrc={fingeringIcon}
              iconAlt=""
              label="Piano fingering"
              iconClassName="h-7 w-7 object-contain"
              pressed={showFingerings}
              onClick={onToggleFingerings}
              dataTour="fingerings"
            />
            <SidePill
              iconSrc={altFingerIcon}
              iconClassName="h-6 w-6 object-contain"
              iconAlt=""
              label="Alternate Fingerings"
              pressed={showAltFingerings}
              onClick={onToggleAlt}
              dataTour="alt"
            />
            {showAltFingerings && (
              <SidePill
                iconSrc={pencilEditIcon}
                iconAlt=""
                label="Edit fingerings"
                pressed={isEditMode}
                disabled={editDisabled}
                onClick={onTogglePencil}
                iconClassName="h-5 w-5 object-contain"
                dataTour="edit"
              />
            )}
          </ConnectedPills>
        )}
        {playbackPill}
        {pitchControl}
      </div>
    </div>
  );
};
